import { useState, useEffect, useCallback } from 'react';
import * as authApi from '@/features/auth/auth.api';
import { useAuthStore } from '@/stores/authStore';

export type FontSizeLevel = 'klein' | 'normaal' | 'groot' | 'extra-groot';

const STORAGE_KEY = 'xxtract-font-scale';

const SCALE_MAP: Record<FontSizeLevel, string> = {
  klein: '0.875',
  normaal: '1',
  groot: '1.125',
  'extra-groot': '1.25',
};

function getInitialLevel(): FontSizeLevel {
  // Wrapped: reading localStorage throws where the browser blocks site data (Safari's "block all
  // cookies", locked-down webviews). This function now also runs before `createRoot`, so an
  // unguarded throw here would white-screen the whole portal instead of misplacing one preference.
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && saved in SCALE_MAP) return saved as FontSizeLevel;
  } catch {
    return 'normaal';
  }
  return 'normaal';
}

function applyFontSize(level: FontSizeLevel) {
  document.documentElement.style.setProperty('--font-scale', SCALE_MAP[level]);
}

/**
 * Applies the stored font size at boot, before React renders anything.
 *
 * The hook below only runs where `FontSizeSelector` is mounted, and that is a single card on the
 * account page. Without this call the setting therefore did nothing anywhere else: a reader who
 * picked "Extra groot" saw large text on the page where she picked it and normal text on every
 * other page, including the one she picked it for. Found on 4 September 2026 while checking the
 * "Extra groot" acceptance criterion of story 4.6 in a real browser.
 */
export function applyStoredFontSize(): void {
  applyFontSize(getInitialLevel());
}

export function useFontSize() {
  const [fontSize, setFontSizeState] = useState<FontSizeLevel>(getInitialLevel);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const setFontSize = useCallback(
    (level: FontSizeLevel) => {
      setFontSizeState(level);
      applyFontSize(level);
      try {
        localStorage.setItem(STORAGE_KEY, level);
      } catch {
        // A blocked store means the choice does not survive a reload. It still applies now, and
        // that is better than throwing inside a click handler.
      }
      if (isAuthenticated) {
        authApi.updatePreferences({ fontSize: level }).catch(() => {});
      }
    },
    [isAuthenticated],
  );

  // Apply on mount
  useEffect(() => {
    applyFontSize(fontSize);
  }, [fontSize]);

  return { fontSize, setFontSize };
}
